import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
import { ICustomerType } from '../constants/customer';
interface IUseFetchConflictChecks {
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number>;
  limit?: number;
  search?: string;
  type?: string;
  customerId?: string;
  fields?: string[];
}
export const useFetchConflictChecks = (params: IUseFetchConflictChecks) => {
  const fetchConflictChecks = async ({ pageParam = 1 }) => {
    const {
      relations: conflictRelations,
      type,
      customerId,
      extraFilters: filters,
      ...rest
    } = params;
    const isCompany = type === ICustomerType.COMPANY;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      queryString.append(`filter.${key}`, String(value));
    });

    (conflictRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    try {
      return await get(
        `${(isCompany ? API_URLS.CONFLICT_CHECKS_COMPANY : API_URLS.CONFLICT_CHECKS_PRIVATE_PERSON).replace(':id', String(customerId))}?${queryString.toString()}`,
      );
    } catch (error) {
      console.error('Error fetching conflict checks:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch conflict checks');
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['conflict-checks', params],
    queryFn: fetchConflictChecks,
    enabled: Boolean(params.customerId),
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
